/**
 * Upload Manager Service
 * Manages chunk upload queue with retry logic and connection recovery
 * Singleton pattern ensures only one instance handles uploads
 */

import axios from 'axios';
import { initDB, getPendingChunks, updateChunkStatus, deleteChunk } from './indexedDB';

class UploadManager {
  constructor() {
    this.uploadQueue = [];
    this.activeUploads = new Map();
    this.maxConcurrentUploads = 3;
    this.retryConfig = {
      maxRetries: 5,
      baseDelay: 10000,  // 10 seconds
      maxDelay: 30000    // 30 seconds
    };
    this.isOnline = navigator.onLine;
    this.isProcessing = false;

    // Network status monitoring
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    console.log('[UploadManager] Initialized');
  }

  /**
   * Enqueue a chunk for upload
   * @param {Object} chunk - Chunk object with id, meeting_id, blob, etc.
   */
  enqueueChunk(chunk) {
    // Check if already in queue
    const existingIndex = this.uploadQueue.findIndex(c => c.id === chunk.id);
    if (existingIndex >= 0) {
      console.log(`[UploadManager] Chunk ${chunk.id} already in queue`);
      return;
    }

    this.uploadQueue.push(chunk);
    console.log(`[UploadManager] Enqueued chunk ${chunk.id}, queue size: ${this.uploadQueue.length}`);

    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  /**
   * Process the upload queue
   */
  async processQueue() {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    console.log('[UploadManager] Starting queue processing');

    while (this.uploadQueue.length > 0 && this.isOnline) {
      // Wait if at max concurrent uploads
      if (this.activeUploads.size >= this.maxConcurrentUploads) {
        await this.waitForSlot();
        continue;
      }

      const chunk = this.uploadQueue.shift();
      if (chunk) {
        // Don't await - upload in background
        this.uploadChunk(chunk);
      }
    }

    this.isProcessing = false;
    console.log('[UploadManager] Queue processing complete');
  }

  /**
   * Upload a single chunk with retry logic
   * @param {Object} chunk - Chunk to upload
   */
  async uploadChunk(chunk) {
    const uploadId = `${chunk.meeting_id}_${chunk.segment_number}_${chunk.chunk_index}`;

    // Check if already uploading
    if (this.activeUploads.has(uploadId)) {
      console.log(`[UploadManager] Upload ${uploadId} already in progress`);
      return;
    }

    this.activeUploads.set(uploadId, true);
    console.log(`[UploadManager] Starting upload: ${uploadId}, active uploads: ${this.activeUploads.size}`);

    try {
      // Update status to 'uploading'
      await updateChunkStatus(chunk.id, 'uploading');

      // Step 1: Request presigned URL from backend
      const urlResponse = await axios.post(
        `/api/livekit/meetings/${chunk.meeting_id}/local-recording/upload-url`,
        {
          participant_identity: chunk.participant_identity,
          segment_number: chunk.segment_number,
          chunk_index: chunk.chunk_index
        },
        { withCredentials: true }
      );

      const { upload_url, s3_key } = urlResponse.data;

      // Step 2: Upload blob to S3 using PUT
      await fetch(upload_url, {
        method: 'PUT',
        body: chunk.blob,
        headers: {
          'Content-Type': 'audio/webm'
        }
      });

      console.log(`[UploadManager] Successfully uploaded chunk ${uploadId} to S3`);

      // Step 3: Notify backend (optional tracking)
      await axios.post(
        `/api/livekit/meetings/${chunk.meeting_id}/local-recording/chunk-uploaded`,
        {
          participant_identity: chunk.participant_identity,
          segment_number: chunk.segment_number,
          chunk_index: chunk.chunk_index,
          s3_key: s3_key,
          has_video: chunk.has_video || false
        },
        { withCredentials: true }
      );

      // Step 4: Delete from IndexedDB (success!)
      await deleteChunk(chunk.id);
      console.log(`[UploadManager] Chunk ${chunk.id} uploaded successfully and removed from IndexedDB`);

    } catch (error) {
      console.error(`[UploadManager] Upload failed for chunk ${uploadId}:`, error);

      // Handle retry logic
      const newRetryCount = (chunk.retry_count || 0) + 1;

      if (newRetryCount < this.retryConfig.maxRetries) {
        // Schedule retry with exponential backoff
        const delay = this.calculateRetryDelay(newRetryCount);
        console.log(`[UploadManager] Scheduling retry ${newRetryCount}/${this.retryConfig.maxRetries} for chunk ${chunk.id} in ${delay}ms`);

        await updateChunkStatus(chunk.id, 'failed', {
          retry_count: newRetryCount,
          last_retry_at: Date.now()
        });

        // Re-enqueue after delay
        setTimeout(() => {
          this.enqueueChunk({ ...chunk, retry_count: newRetryCount });
        }, delay);
      } else {
        // Max retries exceeded
        console.error(`[UploadManager] Max retries exceeded for chunk ${chunk.id}`);
        await updateChunkStatus(chunk.id, 'failed', {
          retry_count: newRetryCount,
          last_retry_at: Date.now()
        });
      }
    } finally {
      this.activeUploads.delete(uploadId);
      console.log(`[UploadManager] Upload ${uploadId} completed, active uploads: ${this.activeUploads.size}`);

      // Continue processing queue if there are more items
      if (this.uploadQueue.length > 0 && !this.isProcessing) {
        this.processQueue();
      }
    }
  }

  /**
   * Calculate retry delay with exponential backoff and jitter
   * @param {number} retryCount - Current retry attempt number
   * @returns {number} - Delay in milliseconds
   */
  calculateRetryDelay(retryCount) {
    // Exponential backoff: 10s, 20s, 30s (capped)
    const delay = Math.min(
      this.retryConfig.baseDelay * Math.pow(2, retryCount - 1),
      this.retryConfig.maxDelay
    );

    // Add jitter (0-1000ms) to prevent thundering herd
    const jitter = Math.random() * 1000;

    return delay + jitter;
  }

  /**
   * Wait for an upload slot to become available
   * @returns {Promise<void>}
   */
  waitForSlot() {
    return new Promise(resolve => {
      const check = () => {
        if (this.activeUploads.size < this.maxConcurrentUploads) {
          resolve();
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    });
  }

  /**
   * Handle online event - retry failed chunks
   */
  handleOnline() {
    console.log('[UploadManager] Network connection restored');
    this.isOnline = true;
    this.retryFailedChunks();
  }

  /**
   * Handle offline event
   */
  handleOffline() {
    console.log('[UploadManager] Network connection lost');
    this.isOnline = false;
  }

  /**
   * Retry all failed chunks
   */
  async retryFailedChunks() {
    try {
      const db = await initDB();
      const tx = db.transaction('chunks', 'readonly');
      const index = tx.objectStore('chunks').index('upload_status');

      const failedRequest = index.getAll('failed');
      const pendingRequest = index.getAll('pending');

      failedRequest.onsuccess = () => {
        const failedChunks = failedRequest.result;
        console.log(`[UploadManager] Retrying ${failedChunks.length} failed chunks`);
        failedChunks.forEach(chunk => this.enqueueChunk(chunk));
      };

      pendingRequest.onsuccess = () => {
        const pendingChunks = pendingRequest.result;
        console.log(`[UploadManager] Retrying ${pendingChunks.length} pending chunks`);
        pendingChunks.forEach(chunk => this.enqueueChunk(chunk));
      };

      failedRequest.onerror = () => {
        console.error('[UploadManager] Error getting failed chunks:', failedRequest.error);
      };

      pendingRequest.onerror = () => {
        console.error('[UploadManager] Error getting pending chunks:', pendingRequest.error);
      };
    } catch (error) {
      console.error('[UploadManager] Error in retryFailedChunks:', error);
    }
  }

  /**
   * Resume uploads for a specific meeting
   * @param {string} meetingId - Meeting ID
   */
  async resumeMeetingUpload(meetingId) {
    try {
      console.log(`[UploadManager] Resuming uploads for meeting ${meetingId}`);
      const pendingChunks = await getPendingChunks(meetingId);
      console.log(`[UploadManager] Found ${pendingChunks.length} pending chunks for meeting ${meetingId}`);

      pendingChunks.forEach(chunk => this.enqueueChunk(chunk));

      if (pendingChunks.length > 0) {
        this.processQueue();
      }
    } catch (error) {
      console.error(`[UploadManager] Error resuming meeting ${meetingId}:`, error);
    }
  }

  /**
   * Get current queue status
   * @returns {Object} - Queue statistics
   */
  getQueueStatus() {
    return {
      queueLength: this.uploadQueue.length,
      activeUploads: this.activeUploads.size,
      isOnline: this.isOnline,
      isProcessing: this.isProcessing
    };
  }
}

// Export singleton instance
export default new UploadManager();
